// Popover and HoverCard: overlays hung off the thing they are about. A Popover is opened by a
// click and can be worked in (a short form, a record's summary with its actions); a HoverCard is a
// glance that follows the pointer and is never interactive. Both measure themselves, take the side
// with room, and stay on screen. docs/operations/design-standard.md, "Controls".

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DOCK_MOTION } from '../ChatBot/flerpieDock';
import { IconButton } from './Button';
import FadeIn from './FadeIn';
import { IconClose } from './icons';
import { useScrollLock } from './DialogScrollLock';
import { MS, usePresence } from './motion';
import Tooltip from './Tooltip';

let seq = 0;

const EDGE = 8;
const GAP = 8;
const PHONE = '(max-width: 639px)';

export interface Box { top: number; bottom: number; left: number; right: number }
export interface Point { x: number; y: number }
/** An element (or a ref to one) the popover follows as it scrolls, or a box or point it opens at once. */
export type PopoverAnchor = React.RefObject<HTMLElement | null> | HTMLElement | Box | Point;
export type Side = 'top' | 'bottom' | 'left' | 'right';
export type Align = 'start' | 'center' | 'end';

export interface Placement {
  left: number;
  top: number;
  side: Side;
  /** The room on the chosen side; the body scrolls past it. */
  maxHeight: number;
  /** Where the anchor sits, for the panel to grow out of. */
  origin: string;
}

const OPPOSITE: Record<Side, Side> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(v, Math.max(lo, hi)));

/**
 * Beside the anchor on `side`, or the opposite side when only that one has room, or whichever has
 * more. The cross axis lines up by `align` and is held inside the viewport.
 */
export function placeOverlay(
  a: Box,
  size: { width: number; height: number },
  view: { width: number; height: number },
  side: Side = 'bottom',
  align: Align = 'start',
): Placement {
  const w = Math.min(size.width, view.width - 2 * EDGE);
  const room: Record<Side, number> = {
    bottom: view.height - EDGE - (a.bottom + GAP),
    top: a.top - GAP - EDGE,
    right: view.width - EDGE - (a.right + GAP),
    left: a.left - GAP - EDGE,
  };
  const need = (s: Side) => (s === 'top' || s === 'bottom' ? size.height : w);
  const other = OPPOSITE[side];
  let chosen: Side = side;
  if (room[side] < need(side)) chosen = room[other] >= need(other) || room[other] > room[side] ? other : side;
  const vertical = chosen === 'top' || chosen === 'bottom';
  const maxHeight = Math.max(0, vertical ? room[chosen] : view.height - 2 * EDGE);
  const h = Math.min(size.height, maxHeight);
  let left: number;
  let top: number;
  if (vertical) {
    top = chosen === 'bottom' ? a.bottom + GAP : a.top - GAP - h;
    const want = align === 'start' ? a.left : align === 'end' ? a.right - w : (a.left + a.right) / 2 - w / 2;
    left = clamp(want, EDGE, view.width - EDGE - w);
  } else {
    left = clamp(chosen === 'right' ? a.right + GAP : a.left - GAP - w, EDGE, view.width - EDGE - w);
    const want = align === 'start' ? a.top : align === 'end' ? a.bottom - h : (a.top + a.bottom) / 2 - h / 2;
    top = clamp(want, EDGE, view.height - EDGE - h);
  }
  const ax = Math.round(clamp((a.left + a.right) / 2 - left, 0, w));
  const ay = Math.round(clamp((a.top + a.bottom) / 2 - top, 0, h));
  const origin = chosen === 'bottom' ? `${ax}px 0px` : chosen === 'top' ? `${ax}px ${Math.round(h)}px` : chosen === 'right' ? `0px ${ay}px` : `${Math.round(w)}px ${ay}px`;
  return { left, top, side: chosen, maxHeight, origin };
}

const isRef = (a: unknown): a is React.RefObject<HTMLElement | null> => !!a && typeof a === 'object' && 'current' in (a as object);
const isElement = (a: unknown): a is HTMLElement => typeof HTMLElement !== 'undefined' && a instanceof HTMLElement;

const anchorElement = (a: PopoverAnchor | null): HTMLElement | null => {
  if (!a) return null;
  if (isRef(a)) return a.current;
  return isElement(a) ? a : null;
};

const anchorBox = (a: PopoverAnchor | null): Box | null => {
  const el = anchorElement(a);
  if (el) {
    const r = el.getBoundingClientRect();
    return { top: r.top, bottom: r.bottom, left: r.left, right: r.right };
  }
  if (!a || isRef(a) || isElement(a)) return null;
  // A DOMRect has x and y too, so the box test goes first.
  if ('top' in a && 'bottom' in a) return { top: a.top, bottom: a.bottom, left: a.left, right: a.right };
  return { top: a.y, bottom: a.y, left: a.x, right: a.x };
};

/** Which anchor this is: a new element or a moved box or point places the popover afresh. */
const anchorKey = (a: PopoverAnchor | null): string => {
  if (!a) return '';
  if (isRef(a) || isElement(a)) return 'element';
  const b = anchorBox(a);
  return b ? `${b.top},${b.bottom},${b.left},${b.right}` : '';
};

const usePhone = (enabled: boolean): boolean => {
  const query = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && !!window.matchMedia(PHONE)?.matches;
  const [phone, setPhone] = useState(() => enabled && query());
  useEffect(() => {
    if (!enabled || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(PHONE);
    if (!mq) return undefined;
    const on = () => setPhone(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, [enabled]);
  return enabled && phone;
};

const DARK = {
  panel: 'bg-surface-container text-on-surface',
  footer: 'bg-surface-container-high',
  meta: 'text-on-surface-variant',
  close: '',
  shadow: 'shadow-overlay',
};
const LIGHT = {
  panel: 'bg-white text-gray-900',
  footer: 'bg-[#F4F4F4]',
  meta: 'text-gray-700',
  close: '!text-gray-700 hover:!text-gray-900',
  shadow: 'shadow-[0_12px_32px_-8px_rgba(15,23,42,0.22)]',
};

const WIDTH = { sm: 'w-64', md: 'w-80', lg: 'w-[26.25rem]' } as const;
const FIELD = 'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled])';

export interface PopoverProps {
  open: boolean;
  /** Escape, a press outside, the close key, focus leaving, or (for a box or point) the page scrolling. */
  onClose: () => void;
  anchor: PopoverAnchor | null;
  title?: React.ReactNode;
  /** The accessible name, when the title is not plain words or there is none. */
  label?: string;
  /** A line under the title: status tags, who made it. */
  meta?: React.ReactNode;
  children?: React.ReactNode;
  /** Actions pinned to the bottom edge one tone step up ("Open event page"). */
  footer?: React.ReactNode;
  /** Prefer this side of the anchor; it flips when only the other side has room. */
  side?: Side;
  /** Line up with the anchor's start, centre or end along the other axis. */
  align?: Align;
  /** sm 16rem, md 20rem (a short form), lg 26.25rem (a record's summary). */
  width?: keyof typeof WIDTH;
  /** On a phone, stay hung off the anchor or rise from the bottom edge as a sheet. */
  phone?: 'anchored' | 'sheet';
  /**
   * A strip on the leading edge in this colour class ("bg-green-500"), for a status the header
   * also says in words. Never the only signal.
   */
  edge?: string;
  /** No close key in the header. */
  hideClose?: boolean;
  /** Stacks above a SideDrawer (z-100), for a popover opened from inside one. */
  aboveDrawer?: boolean;
  /** Where focus goes on close. Without it, the anchor element, else whatever had focus at opening. */
  returnTo?: React.RefObject<HTMLElement | null>;
  /** Drawn on white, for a popover on a day-mode page (the calendar and schedules pass their flag). */
  light?: boolean;
  /** The panel's id, for the trigger's aria-controls. */
  id?: string;
}

export const Popover: React.FC<PopoverProps> = (props) => {
  const { open, anchor, side = 'bottom', align = 'start', phone: phoneMode = 'anchored' } = props;
  const [id] = useState(() => ++seq);
  const [armed, setArmed] = useState(false);
  useEffect(() => { setArmed(true); }, []);
  const { mounted, open: shown } = usePresence(open && armed, MS.exit);
  const sheet = usePhone(phoneMode === 'sheet');

  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Placement | null>(null);
  const latest = useRef(props);
  latest.current = props;
  const last = useRef(props);
  if (open) last.current = props;
  const v = last.current;

  const opener = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  /** Cleared when the popover closes because the person went elsewhere, so focus stays there. */
  const giveFocusBack = useRef(true);
  /** Set by a press inside the popover's React tree, portalled menus within it included. */
  const pressedInside = useRef(false);
  const blurTimer = useRef<number | undefined>(undefined);

  const key = anchorKey(anchor);
  const follows = !!anchorElement(anchor);

  const closeFrom = (elsewhere: boolean) => {
    giveFocusBack.current = !elsewhere;
    latest.current.onClose();
  };

  const place = () => {
    const panel = panelRef.current;
    const a = anchorBox(latest.current.anchor);
    if (!panel || !a || typeof window === 'undefined') return;
    const body = bodyRef.current;
    const natural = panel.offsetHeight + (body ? body.scrollHeight - body.clientHeight : 0);
    const next = placeOverlay(a, { width: panel.offsetWidth, height: natural }, { width: window.innerWidth, height: window.innerHeight }, side, align);
    setPos((p) => (p && p.left === next.left && p.top === next.top && p.maxHeight === next.maxHeight && p.side === next.side ? p : next));
  };

  useLayoutEffect(() => {
    if (!mounted || sheet) return;
    place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, sheet, key, side, align]);

  useEffect(() => {
    if (!open || !mounted || sheet) return undefined;
    // A followed element re-places on scroll and resize; a box or point is stale once the page moves.
    const onScroll = (e: Event) => {
      const t = e.target;
      if (t instanceof Node && panelRef.current?.contains(t)) return;
      if (follows) place(); else closeFrom(true);
    };
    const onResize = () => { if (follows) place(); else closeFrom(true); };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    // Content that loads after opening changes the height and can push the panel off the edge.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => place()) : null;
    if (panelRef.current) ro?.observe(panelRef.current);
    return () => {
      ro?.disconnect();
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mounted, sheet, follows]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: PointerEvent) => {
      if (pressedInside.current) { pressedInside.current = false; return; }
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      // The anchor's own click toggles the popover; closing here first would reopen it.
      if (anchorElement(latest.current.anchor)?.contains(target)) return;
      closeFrom(true);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || panelRef.current?.contains(e.target as Node)) return;
      e.preventDefault();
      closeFrom(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) {
      if (!wasOpen.current) {
        opener.current = document.activeElement as HTMLElement | null;
        giveFocusBack.current = true;
      }
      wasOpen.current = true;
      return;
    }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    window.clearTimeout(blurTimer.current);
    if (!giveFocusBack.current) return;
    const active = document.activeElement;
    const holding = !active || active === document.body || !!panelRef.current?.contains(active);
    if (!holding) return;
    const back = latest.current.returnTo?.current || anchorElement(latest.current.anchor) || opener.current;
    if (back && back.isConnected) back.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => () => window.clearTimeout(blurTimer.current), []);

  // Into the panel once it is placed: a field first, else the panel itself, so its name is read.
  useScrollLock(open && sheet);

  const placed = sheet || !!pos;
  useEffect(() => {
    if (!open || !mounted || !placed) return;
    const panel = panelRef.current;
    if (!panel || panel.contains(document.activeElement)) return;
    const field = Array.from(panel.querySelectorAll<HTMLElement>(FIELD)).find((el) => !el.closest('[aria-hidden="true"]'));
    (field || panel).focus({ preventScroll: true });
  }, [open, mounted, placed]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Escape' || e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
    e.preventDefault();
    // Stopped, so a dialog the popover sits in stays open.
    e.stopPropagation();
    closeFrom(false);
  };

  // Focus that leaves the popover's React tree (Tab past the end, a click on the page) closes it.
  // React's focus events bubble through portals, so a menu opened inside does not count as leaving.
  const onBlur = () => {
    window.clearTimeout(blurTimer.current);
    blurTimer.current = window.setTimeout(() => {
      if (!latest.current.open) return;
      const active = document.activeElement;
      if (!active || active === document.body) return;
      if (panelRef.current?.contains(active)) return;
      if (anchorElement(latest.current.anchor)?.contains(active)) return;
      closeFrom(true);
    }, 0);
  };
  const onFocus = () => window.clearTimeout(blurTimer.current);

  if (!mounted || typeof document === 'undefined') return null;

  const s = v.light ? LIGHT : DARK;
  const titleId = `ui-popover-${id}-title`;
  const width = WIDTH[v.width || 'md'];

  const header = v.title ? (
    <div className="flex flex-none items-start justify-between gap-3 px-4 pb-2 pt-3">
      <div className="min-w-0 flex-1">
        <h2 id={titleId} className="m-0 text-balance text-base font-semibold leading-6 [overflow-wrap:anywhere]">{v.title}</h2>
        {v.meta ? <div className={`mt-1 flex min-w-0 flex-wrap items-center gap-2 text-xs ${s.meta}`}>{v.meta}</div> : null}
      </div>
      {v.hideClose ? null : (
        <Tooltip label="Close">
          <IconButton label="Close" size="sm" onClick={() => closeFrom(false)} className={`-mr-1.5 ${s.close}`}>
            <IconClose />
          </IconButton>
        </Tooltip>
      )}
    </div>
  ) : null;

  const content = (
    <>
      {header}
      <div ref={bodyRef} className={`min-h-0 flex-1 overflow-y-auto px-4 pb-4 ${v.title ? 'pt-0.5' : 'pt-3'}`}>{v.children}</div>
      {v.footer ? (
        <div className={`flex flex-none flex-wrap items-center justify-end gap-2 rounded-b-overlay px-4 py-2.5 ${sheet ? 'pb-[max(0.75rem,env(safe-area-inset-bottom))]' : ''} ${s.footer}`}>
          {v.footer}
        </div>
      ) : null}
    </>
  );

  const shared = {
    ref: panelRef,
    id: v.id || `ui-popover-${id}`,
    role: 'dialog',
    'aria-labelledby': v.label || !v.title ? undefined : titleId,
    'aria-label': v.label || (v.title ? undefined : 'Details'),
    'aria-hidden': open ? undefined : true,
    tabIndex: -1,
    onKeyDown,
    onBlur,
    onFocus,
    // Cleared on the next tick too, in case the press never reaches the document.
    onPointerDown: () => { pressedInside.current = true; window.setTimeout(() => { pressedInside.current = false; }, 0); },
  } as const;

  if (sheet) {
    return createPortal(
      <div className={`fixed inset-y-0 left-[var(--flerpie-dock,0px)] right-0 ${v.aboveDrawer ? 'z-[110]' : 'z-50'} flex items-end pt-12 transition-[left] ${DOCK_MOTION} ${open ? '' : 'pointer-events-none'}`}>
        <div
          aria-hidden="true"
          onClick={() => closeFrom(true)}
          className={`scrim transition-opacity ${shown ? 'opacity-100 duration-standard ease-emphasized' : 'opacity-0 duration-exit ease-exit'}`}
        />
        <div
          {...shared}
          className={`relative flex max-h-full w-full flex-col rounded-t-overlay outline-none ${s.shadow} ${s.panel} transition-[transform,opacity] motion-reduce:translate-y-0 motion-reduce:transition-[opacity] ${
            shown ? 'translate-y-0 opacity-100 duration-spring ease-spring' : 'translate-y-full opacity-0 duration-exit ease-exit'
          }`}
        >
          {v.edge ? <div aria-hidden="true" className={`absolute inset-x-0 top-0 h-1 rounded-t-overlay ${v.edge}`} /> : null}
          {content}
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      {...shared}
      style={{ left: pos?.left ?? 0, top: pos?.top ?? 0, maxHeight: pos?.maxHeight, transformOrigin: pos?.origin }}
      className={`fixed ${v.aboveDrawer ? 'z-[110]' : 'z-50'} flex max-w-[calc(100vw-1rem)] flex-col rounded-overlay outline-none ${width} ${s.shadow} ${s.panel} transition-[transform,opacity] motion-reduce:scale-100 motion-reduce:transition-[opacity] ${
        shown && pos ? 'scale-100 opacity-100 duration-standard ease-emphasized' : 'pointer-events-none scale-[0.96] opacity-0 duration-exit ease-exit'
      }`}
    >
      {v.edge ? <div aria-hidden="true" className={`absolute inset-y-0 left-0 w-1 rounded-l-overlay ${v.edge}`} /> : null}
      {content}
    </div>,
    document.body,
  );
};

// ── HoverCard ────────────────────────────────────────────────────────────────

const PAD = 12;

export interface HoverAt {
  /** The pointer, or the hovered box's centre line. */
  x: number;
  y: number;
  /** The hovered box's top and bottom edges. With them the card sits clear of the box, never over it. */
  boxTop?: number;
  boxBottom?: number;
}

/** Above the box or the pointer, below when there is no room, centred on x and held on screen. */
export function placeHoverCard(at: HoverAt, size: { width: number; height: number }, view: { width: number; height: number }): { left: number; top: number; below: boolean } {
  const left = clamp(at.x - size.width / 2, PAD, view.width - PAD - size.width);
  let top: number;
  let below: boolean;
  if (at.boxTop != null && at.boxBottom != null) {
    below = at.boxTop - GAP - size.height < PAD;
    top = below ? at.boxBottom + GAP : at.boxTop - GAP - size.height;
  } else {
    below = at.y - 12 - size.height < PAD;
    top = below ? at.y + 18 : at.y - 12 - size.height;
  }
  return { left, top: clamp(top, PAD, view.height - PAD - size.height), below };
}

export interface HoverCardProps {
  /** Where the hover is. Null puts the card away. */
  at: HoverAt | null;
  children?: React.ReactNode;
  /** What the card is showing; a new key fades the new content in. */
  contentKey?: string | number | null;
  /** For the hovered element's aria-describedby, where it is focusable. */
  id?: string;
  /** Drawn on white, for a card on a day-mode page. */
  light?: boolean;
}

export const HoverCard: React.FC<HoverCardProps> = ({ at, children, contentKey, id, light }) => {
  const { mounted, open } = usePresence(!!at, MS.exit);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; below: boolean } | null>(null);
  const lastAt = useRef(at);
  const lastChildren = useRef(children);
  const lastKey = useRef(contentKey);
  if (at) { lastAt.current = at; lastChildren.current = children; lastKey.current = contentKey; }
  const where = lastAt.current;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!mounted || !el || !where || typeof window === 'undefined') return;
    const next = placeHoverCard(where, { width: el.offsetWidth, height: el.offsetHeight }, { width: window.innerWidth, height: window.innerHeight });
    setPos((p) => (p && p.left === next.left && p.top === next.top && p.below === next.below ? p : next));
  }, [mounted, where?.x, where?.y, where?.boxTop, where?.boxBottom, lastKey.current]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!mounted || typeof document === 'undefined') return null;
  const surface = light
    ? 'bg-white text-gray-900 shadow-[0_8px_24px_-6px_rgba(15,23,42,0.18)]'
    : 'bg-surface-container-highest text-on-surface shadow-overlay';

  return createPortal(
    <div
      ref={ref}
      id={id}
      role="tooltip"
      style={{ left: pos?.left ?? 0, top: pos?.top ?? 0 }}
      className={`pointer-events-none fixed z-[10001] max-w-[17rem] rounded-overlay px-2.5 py-1.5 text-xs leading-4 ${surface} transition-[opacity,transform] motion-reduce:translate-y-0 motion-reduce:transition-[opacity] ${
        open && pos
          ? 'translate-y-0 opacity-100 duration-standard ease-emphasized'
          : `opacity-0 duration-exit ease-exit ${pos?.below ? '-translate-y-1' : 'translate-y-1'}`
      }`}
    >
      <FadeIn key={lastKey.current ?? 'card'}>{lastChildren.current}</FadeIn>
    </div>,
    document.body,
  );
};

export default Popover;
